import { Box, Stack } from "@mui/material";
import { CardComponent } from "./card";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import { GoMakeCurrency } from "@/icons/go-make-currency";
import { EStatisticsLabels, QUOTE_STATUSES } from "../../enums";
import { useUserPermission } from "@/hooks/use-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { useIsMobile } from "@/hooks/use-responsive";
import { useTheme } from "@/contexts/theme";
import { CreateNewButton } from "./create-new-button";

export interface ICard {
  key: string;
  value: string;
}

interface ICardsSection {
  statistics: ICard[];
  activeCard: any;
  onClick: () => void;
  onClickCard: (value1, value2) => void;
  onSecondClickCard: () => void;
  /** Mobile renders "Create new" in the page title row instead. */
  showCreateNew?: boolean;
}

const getValueByKey = (statistics: ICard[], key: string) => {
  const card = statistics?.find((item) => item.key === key);
  return card ? card.value : "";
};

const CardsSection = ({ showCreateNew = true, ...props }: ICardsSection) => {
  const { t } = useTranslation();
  const { classes } = useStyle();
  const isMobile = useIsMobile();
  const isModern = useTheme().currentTheme === "modern" && !isMobile;
  const { primaryColor, secondColor, warningColor, successColor, errorColor } =
    useGomakeTheme();
  const { CheckPermission } = useUserPermission();

  const waitingCard = (
    <CardComponent
      key={EStatisticsLabels.WAITING}
      compact={isMobile}
      onClick={() =>
        props.onClickCard(EStatisticsLabels.WAITING, QUOTE_STATUSES.Waiting)
      }
      isActive={props.activeCard === EStatisticsLabels.WAITING}
      text={t("sales.quote.waiting")}
      number={getValueByKey(props.statistics, "waitingCount")}
      textColor={`var(--theme-card-pending, ${warningColor(300)})`}
      tone="waiting"
      icon={<HourglassTopIcon />}
      onSecondClick={props.onSecondClickCard}
    />
  );

  const approvedCard = (
    <CardComponent
      key={EStatisticsLabels.APPROVED}
      compact={isMobile}
      onClick={() =>
        props.onClickCard(EStatisticsLabels.APPROVED, QUOTE_STATUSES.Approved)
      }
      isActive={props.activeCard === EStatisticsLabels.APPROVED}
      text={t("sales.quote.approved")}
      number={getValueByKey(props.statistics, "approvedCount")}
      textColor={`var(--theme-card-approved, ${successColor(300)})`}
      tone="approved"
      icon={<DoneAllIcon />}
      onSecondClick={props.onSecondClickCard}
    />
  );

  const rejectedCard = (
    <CardComponent
      key={EStatisticsLabels.CANCELED}
      compact={isMobile}
      onClick={() =>
        props.onClickCard(EStatisticsLabels.CANCELED, QUOTE_STATUSES.Canceled)
      }
      isActive={props.activeCard === EStatisticsLabels.CANCELED}
      text={t("sales.quote.rejected")}
      number={getValueByKey(props.statistics, "canceledCount")}
      textColor={errorColor(300)}
      tone="urgent"
      icon={<DoDisturbAltIcon />}
      onSecondClick={props.onSecondClickCard}
    />
  );

  const successRateCard = (
    <CardComponent
      key="successRate"
      compact={isMobile}
      text={t("sales.quote.successRate")}
      number={getValueByKey(props.statistics, "successRate") + "%"}
      textColor={`var(--theme-card-success-rate, ${primaryColor(300)})`}
      tone="time"
      icon={<TrendingUpIcon />}
    />
  );

  const totalPriceCard = (
    <CardComponent
      key="totalPrice"
      compact={isMobile}
      text={t("sales.quote.totalPrice")}
      number={getValueByKey(props.statistics, "totalPrice")}
      textColor={secondColor(300)}
      tone="ink"
      icon={<GoMakeCurrency color={isModern ? "currentColor" : secondColor(300)} />}
    />
  );

  const canSeeStatistics = CheckPermission(Permissions.SHOW_QUOTE_STATISTICS);

  if (isMobile) {
    if (!canSeeStatistics && !showCreateNew) return null;

    return (
      <Stack width="100%" minWidth={0} gap="8px">
        {canSeeStatistics && (
          <Box sx={classes.statisticsScroller}>
            {totalPriceCard}
            {successRateCard}
            {waitingCard}
            {approvedCard}
            {rejectedCard}
          </Box>
        )}
        {showCreateNew && <CreateNewButton onClick={props.onClick} />}
      </Stack>
    );
  }

  return (
    <Stack
      className={isModern ? "gm-pf-kpis" : undefined}
      direction={"row"}
      gap={isModern ? "8px" : "5px"}
      alignItems={"center"}
      flexWrap={isModern ? "nowrap" : "wrap-reverse"}
    >
      {canSeeStatistics && (
        <>
          {waitingCard}
          {approvedCard}
          {rejectedCard}
          {!isModern && <div style={classes.verticalLine} />}
          {successRateCard}
          {totalPriceCard}
        </>
      )}
      {showCreateNew && <CreateNewButton onClick={props.onClick} />}
    </Stack>
  );
};

export { CardsSection };
